import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import PageHeader from 'components/pageHeader'
import { Container, Divider, IconButton, Inline, Stack, useColorMode } from '@airbus/components-react/components'
import WidgetPlanning from './components/widgets/WidgetPlanning/widgetPlanning'
import WidgetProgression from './components/widgets/WidgetProgression/widgetProgression'
import WidgetWorkload from './components/widgets/WidgetWorkload/widgetWorkload'
import WidgetProgressByType from './components/widgets/WidgetProgressByType/widgetProgressByType'
import WidgetSummary from './components/widgets/WidgetSummary/widgetSummary'
import WidgetListTasks from './components/widgets/WidgetListTasks/widgetListTasks'

import DraggableCollapsibleItem from 'components/DraggableCollapsibleItem'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useMediaQuery from '@airbus/components-react/lib/hooks/useMediaQuery'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesDown, faAnglesUp } from '@fortawesome/free-solid-svg-icons'

const {
  BDV_WDG_PLANNING_UNAME,
  BDV_WDG_PROGRESSION_UNAME,
  BDV_WDG_TYPE_TASK_UNAME,
  BDV_WDG_LIST_TASKS_UNAME,
  BDV_STORE_NAME,
} = ComponentsUNames

const SituationBDVWidgets = ({
  id,
  pageUniqueName,
  bdvnumdoc,
  handleBDVChange,
  summary,
  cellName,
  handleSummaryChange,
}) => {
  const {
    responsive: { breakpoint },
  } = useColorMode()

  const mqMaxMD = useMediaQuery(`(max-width: ${breakpoint.md}px)`) // true si la largeur d'écran est inférieur à lg : 1200px
  const Wrapper = mqMaxMD ? Stack : Inline

  switch (id) {
    case BDV_WDG_PLANNING_UNAME:
      return <WidgetPlanning pageUniqueName={pageUniqueName} bdvnumdoc={bdvnumdoc} handleBDVChange={handleBDVChange} />
    case BDV_WDG_PROGRESSION_UNAME:
      return (
        <Wrapper spacing='1-x' alignY='start' wrapChildren={false}>
          <div style={{ flex: 1 }}>
            <WidgetProgression bdvnumdoc={bdvnumdoc} />
          </div>
          <Divider vertical={!mqMaxMD} />
          <div style={{ flex: 1 }}>
            <WidgetWorkload bdvnumdoc={bdvnumdoc} />
          </div>
        </Wrapper>
      )

    case BDV_WDG_TYPE_TASK_UNAME:
      return (
        <Wrapper spacing='1-x' alignY='start' wrapChildren={false}>
          <div style={{ flex: 1 }}>
            <WidgetProgressByType bdvnumdoc={bdvnumdoc} />
          </div>
          <Divider vertical={!mqMaxMD} />
          <div style={{ flex: 1 }}>
            <WidgetSummary
              bdvnumdoc={bdvnumdoc}
              summary={summary}
              cellName={cellName}
              handleSummaryChange={handleSummaryChange}
            />
          </div>
        </Wrapper>
      )

    case BDV_WDG_LIST_TASKS_UNAME:
      return <WidgetListTasks pageUniqueName={pageUniqueName} bdvnumdoc={bdvnumdoc} summary={summary} />
  }
}

const ActionButtons = ({ isAllExpanded, onExpandAll }) => {
  const { t } = useTranslation()

  return (
    <IconButton
      variant='ghost'
      size='xsmall'
      onClick={onExpandAll}
      title={isAllExpanded ? t('collapse_all') : t('expand_all')}
    >
      <FontAwesomeIcon icon={isAllExpanded ? faAnglesUp : faAnglesDown} />
    </IconButton>
  )
}

const WorkPackagePage = () => {
  const { t } = useTranslation()
  const pageUniqueName = BDV_STORE_NAME
  const [items, setItems] = useState([
    {
      id: BDV_WDG_PLANNING_UNAME,
      expanded: LocalStorage.getStoredExpanded(BDV_WDG_PLANNING_UNAME, BDV_STORE_NAME),
      titleKey: '7001712',
      isLocked: true,
    },
    ...[
      {
        id: BDV_WDG_PROGRESSION_UNAME,
        expanded: LocalStorage.getStoredExpanded(BDV_WDG_PROGRESSION_UNAME, BDV_STORE_NAME),
        titleKey: 'workpackage_stats',
        defaultIndex: 1,
      },
      {
        id: BDV_WDG_TYPE_TASK_UNAME,
        expanded: LocalStorage.getStoredExpanded(BDV_WDG_TYPE_TASK_UNAME, BDV_STORE_NAME),
        titleKey: 'workpackage_task_detail',
        defaultIndex: 2,
      },
      {
        id: BDV_WDG_LIST_TASKS_UNAME,
        expanded: LocalStorage.getStoredExpanded(BDV_WDG_LIST_TASKS_UNAME, BDV_STORE_NAME),
        titleKey: '9500019',
        defaultIndex: 3,
      },
    ].sort(
      (a, b) =>
        LocalStorage.getStoredIndex(a.id, a.defaultIndex, BDV_STORE_NAME) -
        LocalStorage.getStoredIndex(b.id, b.defaultIndex, BDV_STORE_NAME),
    ),
  ])
  const [searchParams, setSearchParams] = useSearchParams()
  const aircraft = searchParams.get('search')

  const moveItem = useCallback(
    (fromIndex, toIndex) => {
      setItems((item) => {
        const updatedItems = [...item]
        const [movedItem] = updatedItems.splice(fromIndex, 1)
        updatedItems.splice(toIndex, 0, movedItem)
        // Sync positions with local storage
        updatedItems.forEach((item, i) => {
          const propToUpdate = [LocalStorage.KEY_PREFS, pageUniqueName, item.id + '|POSITION']
          LocalStorage.put(propToUpdate, i)
        })
        return updatedItems
      })
    },
    [items],
  )

  const handleExpandedChange = (id, expanded) => {
    // Sync with local storage
    const propToUpdate = [LocalStorage.KEY_PREFS, pageUniqueName, id + '|EXPANDED']
    LocalStorage.put(propToUpdate, expanded)
    setItems(items.map((item) => (item.id === id ? { ...item, expanded } : item)))
  }

  const handleExpandAll = () => {
    const allExpanded = items.every((item) => item.expanded)

    // Sync with local storage
    items.forEach(({ id }) =>
      LocalStorage.put([LocalStorage.KEY_PREFS, pageUniqueName, id + '|EXPANDED'], !allExpanded),
    )
    setItems(items.map((item) => ({ ...item, expanded: !allExpanded })))
  }

  const { bdvnumdoc } = useParams()

  const navigate = useNavigate()
  const baseUrl = process.env.PUBLIC_URL

  const handleBDVChange = (wp, ac) => {
    navigate(baseUrl + '/workpackage/' + wp + '?search=' + ac, { preventScrollReset: true })
  }

  const handleSummaryChange = (sm, name) => {
    if (bdvnumdoc) {
      setSummary(sm)
      setCellName(name)
    }
  }

  const [hoverIndex, setHoverIndex] = useState(null)
  const [dropPosition, setDropPosition] = useState(null)
  const [summary, setSummary] = useState(null)
  const [cellName, setCellName] = useState(null)

  let navItems = [
    {
      title: t('9500092'),
      url: baseUrl + '/workpackage',
    },
  ]

  if (bdvnumdoc) {
    navItems.push({
      title: `${aircraft ? aircraft + ' - ' : ''}${bdvnumdoc}`,
      url: baseUrl + `/workpackage/${bdvnumdoc}`,
    })
  }

  return (
    <>
      <PageHeader
        pageTitle={t('9500092')}
        navItems={navItems}
        ActionButtons={() => (
          <ActionButtons isAllExpanded={items.every((item) => item.expanded)} onExpandAll={handleExpandAll} />
        )}
      />
      <Container style={{ flex: 1 }} until='xxl'>
        <Stack spacing='2-x'>
          {items?.map(({ id, expanded, isLocked, titleKey }, index) => (
            <DraggableCollapsibleItem
              key={id}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
              dropPosition={dropPosition}
              setDropPosition={setDropPosition}
              index={index}
              moveListItem={moveItem}
              isLocked={isLocked}
              title={t(titleKey)}
              expanded={expanded}
              onExpandedChange={(exp) => handleExpandedChange(id, exp)}
            >
              <SituationBDVWidgets
                id={id}
                pageUniqueName={pageUniqueName}
                bdvnumdoc={bdvnumdoc}
                handleBDVChange={handleBDVChange}
                summary={summary}
                cellName={cellName}
                handleSummaryChange={handleSummaryChange}
              />
            </DraggableCollapsibleItem>
          ))}
        </Stack>
      </Container>
    </>
  )
}

export default WorkPackagePage
